import { useTranslation } from 'react-i18next';
import { Card } from '@parrot-software/design-system';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { getLastPaymentErrorContent } from '@subscriptions/presentation/utils';

interface BlockLastPaymentStatusCardProps {
  lastError: NonNullable<SubscriptionBlockViewDto['lastError']>;
}

/**
 * BlockLastPaymentStatusCard - Right-column card surfacing the status of the last
 * failed payment for a subscription block.
 *
 * Rendered below the payment method card only when the block has a `lastError`.
 * Uses the mapped error badge as the card title and the mapped description as the
 * body. Reuses the card pattern of the payment method card.
 *
 * @param {Object} props - Component properties
 * @param {NonNullable<SubscriptionBlockViewDto['lastError']>} props.lastError - Last payment error data
 * @returns {React.ReactElement} Last payment status card component
 *
 * @example
 * <BlockLastPaymentStatusCard lastError={block.lastError} />
 */
export const BlockLastPaymentStatusCard = ({ lastError }: BlockLastPaymentStatusCardProps) => {
  const { t, i18n } = useTranslation('subscriptions');
  const { badgeText, description } = getLastPaymentErrorContent(lastError, t, i18n);

  return (
    <Card className="border-destructive bg-destructive/10 p-6 text-destructive shadow-xs">
      <div className="flex flex-col gap-1.5">
        <p className="text-sm text-destructive font-bold">{badgeText}</p>
        <p className="text-sm">{description}</p>
      </div>
    </Card>
  );
};
